/**
 * Crazy Mode Context
 * Toggle for fun/ludic events in HAL 3000
 */
import { createContext, useContext, useState, useEffect } from 'react';

const CrazyModeContext = createContext();

export const useCrazyMode = () => {
  const context = useContext(CrazyModeContext);
  if (!context) {
    throw new Error('useCrazyMode must be used within CrazyModeProvider');
  }
  return context;
};

export const CrazyModeProvider = ({ children }) => {
  // Load from localStorage, default to true (crazy mode ON by default)
  const [crazyMode, setCrazyMode] = useState(() => {
    const stored = localStorage.getItem('hal_crazy_mode');
    return stored !== null ? stored === 'true' : true;
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem('hal_crazy_mode', crazyMode.toString());
  }, [crazyMode]);

  const toggleCrazyMode = () => {
    setCrazyMode(prev => !prev);
  };

  return (
    <CrazyModeContext.Provider value={{ crazyMode, setCrazyMode, toggleCrazyMode }}>
      {children}
    </CrazyModeContext.Provider>
  );
};

/**
 * Crazy Mode Toggle Component - Apollo Style Vintage Switch
 */
export const CrazyModeToggle = () => {
  const { crazyMode, toggleCrazyMode } = useCrazyMode();

  return (
    <div className="flex flex-col items-center bg-black/50 rounded p-1 border border-gray-700">
      {/* Label */}
      <div className="text-[7px] font-mono text-center leading-tight mb-0.5">
        <div className={crazyMode ? 'text-red-400' : 'text-gray-500'}>CRAZY</div>
        <div className={crazyMode ? 'text-red-400' : 'text-gray-500'}>MODE</div>
      </div>
      
      {/* Apollo-style vintage toggle switch */}
      <button
        onClick={toggleCrazyMode}
        className="relative w-5 h-8 cursor-pointer focus:outline-none"
        data-testid="crazy-mode-toggle"
        title={crazyMode ? 'Désactiver mode ludique' : 'Activer mode ludique'}
      >
        {/* Switch housing - metal background */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-600 to-gray-900 rounded-sm border border-gray-500 shadow-inner" />
        
        {/* Toggle lever */}
        <div 
          className={`absolute left-1/2 -translate-x-1/2 w-3.5 h-4 rounded-sm shadow-lg transition-all duration-100 ${
            crazyMode 
              ? 'top-0.5 bg-gradient-to-b from-red-400 to-red-600 border border-red-300' 
              : 'bottom-0.5 bg-gradient-to-b from-gray-300 to-gray-500 border border-gray-200'
          }`}
          style={{
            boxShadow: crazyMode 
              ? '0 2px 3px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.4)' 
              : '0 -2px 3px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.4)'
          }}
        >
          {/* Grip lines */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 space-y-px">
            <div className="h-px bg-black/40" />
            <div className="h-px bg-black/40" />
          </div>
        </div>
      </button>
      
      {/* Status indicator */}
      <div className={`text-[6px] font-bold mt-0.5 ${crazyMode ? 'text-green-400' : 'text-gray-600'}`}>
        {crazyMode ? 'ON' : 'OFF'}
      </div>
    </div>
  );
};

export default CrazyModeContext;
